import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import {
  getModerationReports, updateReportStatus, resolveUncheckedSpot,
  updateSpot, maskSpot, deletePhoto, getPhotoUrl, getModerationMyStats
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, CheckCircle, Loader2, X, Pencil, Trash2 } from "lucide-react";

const PREDEFINED_TAGS = [
  { label: "Environnement", tags: ["Nature", "Bord de l'eau", "Urbain"] },
  { label: "Ambiance",      tags: ["Discret", "Fréquenté", "Nocturne", "Diurne"] },
  { label: "Profil",        tags: ["Hommes", "Mixte", "Naturiste"] },
];

const CATEGORIES = [
  { value: "facile",     label: "Facile" },
  { value: "audacieux",  label: "Audacieux" },
  { value: "temporaire", label: "Temporaire" },
];

const TYPE_CONFIG = {
  user_report:           { label: "Signalement utilisateur",        dot: "🔴", className: "bg-red-500/20 text-red-300 border-red-500/40" },
  auto_moderation_image: { label: "Modération image (IA)",          dot: "🟠", className: "bg-orange-500/20 text-orange-300 border-orange-500/40" },
  auto_moderation:       { label: "Modération texte (IA)",          dot: "🟡", className: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  unchecked:             { label: "Non vérifié (IA indispo)",        dot: "⚪", className: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40" },
  unchecked_text:        { label: "Non vérifié texte (IA indispo)",  dot: "⚪", className: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40" },
  unchecked_image:       { label: "Non vérifié image (IA indispo)",  dot: "⚪", className: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40" },
};

export default function ModerationPage() {
  const { isAdmin, isModerator, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [myStats, setMyStats] = useState(null);

  // Edit dialog
  const [editingItem, setEditingItem] = useState(null);
  const [editPhotos, setEditPhotos] = useState([]);
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "", tags: [] });
  const [saving, setSaving] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);

  // Delete dialog
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Photo viewer
  const [viewingPhoto, setViewingPhoto] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || (!isAdmin && !isModerator)) {
      navigate("/");
      return;
    }
    fetchItems();
    getModerationMyStats()
      .then(data => setMyStats(data.resolved_count))
      .catch(() => {});
  }, [isAuthenticated, isAdmin, isModerator, authLoading]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getModerationReports();
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (item) => {
    setResolving(item.id);
    try {
      if (item.type === "unchecked") {
        await resolveUncheckedSpot(item.spot_id);
      } else {
        await updateReportStatus(item.id, "resolved");
      }
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      console.error(e);
    } finally {
      setResolving(null);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditPhotos(item.photos || []);
    setEditForm({
      title: item.spot_title || "",
      description: item.spot_description || "",
      category: item.spot_category || "",
      tags: item.spot_tags || [],
    });
  };

  const toggleTag = (tag) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await updateSpot(editingItem.spot_id, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category || undefined,
        tags: editForm.tags,
      });
      if (editingItem.type === "unchecked") {
        await resolveUncheckedSpot(editingItem.spot_id);
      } else {
        await updateReportStatus(editingItem.id, "resolved");
      }
      setItems(prev => prev.filter(i => i.id !== editingItem.id));
      setEditingItem(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhoto = async (filename) => {
    if (!editingItem) return;
    setDeletingPhoto(filename);
    try {
      await deletePhoto(editingItem.spot_id, filename);
      setEditPhotos(prev => prev.filter(p => !p.endsWith("/" + filename)));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleDeleteOrMaskSpot = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await maskSpot(deletingItem.spot_id);
      if (deletingItem.type === "unchecked") {
        await resolveUncheckedSpot(deletingItem.spot_id);
      } else {
        await updateReportStatus(deletingItem.id, "resolved");
      }
      setItems(prev => prev.filter(i => i.id !== deletingItem.id));
      setDeletingItem(null);
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) return null;
  if (!isAuthenticated || (!isAdmin && !isModerator)) return null;

  return (
    <div className="min-h-screen bg-[#1d191a] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">

        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Carte
          </Button>
          <h1 className="text-xl font-semibold">Modération</h1>
          {!loading && (
            <Badge className="bg-white/10 text-white/70 border border-white/20">
              {items.length} en attente
            </Badge>
          )}
          {myStats !== null && (
            <span className="text-xs text-white/40 ml-auto">
              {myStats} traité{myStats !== 1 ? "s" : ""} par vous
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
            <p>Aucun élément en attente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.user_report;
              return (
                <div
                  key={item.id}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-xs border ${cfg.className}`}>
                      {cfg.dot} {cfg.label}
                    </Badge>
                    {item.spot_title && (
                      <span className="text-sm font-medium text-white/90">{item.spot_title}</span>
                    )}
                  </div>

                  {item.reason && (
                    <p className="text-sm text-white/60">{item.reason}</p>
                  )}

                  {item.submitted_text && (
                    <p className="text-xs text-white/40 italic line-clamp-2">
                      « {item.submitted_text} »
                    </p>
                  )}

                  {item.photos && item.photos.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {item.photos.map(photo => (
                        <img
                          key={photo}
                          src={getPhotoUrl(photo)}
                          alt=""
                          className="w-14 h-14 object-cover rounded cursor-pointer opacity-80 hover:opacity-100 transition-opacity"
                          onClick={() => setViewingPhoto(getPhotoUrl(photo))}
                        />
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-white/30">
                    {new Date(item.created_at).toLocaleString("fr-FR")}
                  </p>

                  <div className="flex gap-2 flex-wrap pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleValidate(item)}
                      disabled={resolving === item.id}
                      className="border-green-500/40 text-green-400 hover:bg-green-500/10 hover:text-green-300"
                    >
                      {resolving === item.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : "Valider"
                      }
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(item)}
                      className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeletingItem(item)}
                      className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Masquer
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Dialog d'édition ── */}
      <Dialog open={!!editingItem} onOpenChange={open => !open && setEditingItem(null)}>
        <DialogContent className="bg-zinc-950 text-white border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le spot</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Titre</Label>
              <Input
                value={editForm.title}
                onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="bg-white/5 border-white/10 text-white resize-none"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Catégorie</Label>
              <div className="flex gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setEditForm(prev => ({ ...prev, category: cat.value }))}
                    className={`px-3 py-1 rounded text-xs border transition-colors ${
                      editForm.category === cat.value
                        ? "bg-amber-500/20 border-amber-500/60 text-amber-300"
                        : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70 text-xs">Tags</Label>
              {PREDEFINED_TAGS.map(({ label, tags }) => (
                <div key={label}>
                  <p className="text-xs text-white/40 mb-1">{label}</p>
                  <div className="flex gap-3 flex-wrap">
                    {tags.map(tag => (
                      <label key={tag} className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={editForm.tags.includes(tag)}
                          onCheckedChange={() => toggleTag(tag)}
                          className="border-white/20"
                        />
                        <span className="text-xs text-white/70">{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {editPhotos.length > 0 && (
              <div className="space-y-1">
                <Label className="text-white/70 text-xs">Photos</Label>
                <div className="flex gap-2 flex-wrap">
                  {editPhotos.map(photo => {
                    const filename = photo.split("/").pop();
                    return (
                      <div key={photo} className="relative group">
                        <img
                          src={getPhotoUrl(photo)}
                          alt=""
                          className="w-16 h-16 object-cover rounded cursor-pointer"
                          onClick={() => setViewingPhoto(getPhotoUrl(photo))}
                        />
                        <button
                          onClick={() => handleDeletePhoto(filename)}
                          disabled={deletingPhoto === filename}
                          className="absolute -top-1.5 -right-1.5 bg-red-600 rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {deletingPhoto === filename
                            ? <Loader2 className="w-2.5 h-2.5 animate-spin text-white" />
                            : <X className="w-2.5 h-2.5 text-white" />
                          }
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setEditingItem(null)}
              className="text-white/60 hover:text-white"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sauvegarder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog de confirmation de suppression ── */}
      <Dialog open={!!deletingItem} onOpenChange={open => !open && setDeletingItem(null)}>
        <DialogContent className="bg-zinc-950 text-white border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle>Masquer ce spot ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60 py-2">
            Le spot sera masqué et ne sera plus visible sur la carte. Un admin pourra le réactiver depuis /admin.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeletingItem(null)}
              className="text-white/60 hover:text-white"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteOrMaskSpot}
              disabled={deleting}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masquer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Visionneuse photo plein écran ── */}
      {viewingPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setViewingPhoto(null)}
        >
          <img
            src={viewingPhoto}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setViewingPhoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
